/**
 * InfluenceMap — 2D world-space bend texture for foliage interaction.
 *
 * Each cell stores a 2-component bend vector (R = bendX, G = bendZ) in
 * normalized world-space units. Agents deposit by adding their contribution
 * to a circular footprint around their position; each frame, the entire
 * map is multiplied by an exponential decay factor so old bends fade out.
 *
 * Storage: Float32Array RG (2 components × N²). For GPU upload, the
 * Float32Array is packed into a half-float texture (RGBA16F with B/A unused;
 * RG16F has driver caveats so we go with the universally-supported RGBA16F).
 *
 * Why CPU-side: the agent footprint is a few cells (radius ≈ 5..20 cells)
 * and there are at most ~256 agents per frame, so total work is < 100k
 * cell writes per frame. Cheap. The decay multiply is the only full-map
 * pass; v2 can move both to GPU compute when needed.
 */

import * as THREE from 'three/webgpu';
import type { InfluenceMapConfig, InfluenceAgent } from './InfluenceMapConfig.js';

export interface InfluenceMapStats {
  /** Number of agents deposited in the most recent step. */
  agentsThisFrame: number;
  /** Sum of |bend| across all cells (gross activity indicator). */
  totalEnergy: number;
  /** Max |bend| of any single cell (clipping indicator). */
  maxBend: number;
  /** Wall-clock ms for last full update. */
  updateTimeMs: number;
}

export class InfluenceMap {
  readonly config: InfluenceMapConfig;
  readonly N: number;

  /** Bend vectors: 2 floats per cell (X, Z components). */
  readonly bend: Float32Array;
  /** GPU texture (RGBA16F, half-float). */
  readonly texture: THREE.DataTexture;

  private _gpuBuffer: Uint16Array;
  private _stats: InfluenceMapStats = {
    agentsThisFrame: 0, totalEnergy: 0, maxBend: 0, updateTimeMs: 0,
  };

  constructor(config: InfluenceMapConfig) {
    this.config = config;
    this.N = config.quality.resolution;
    this.bend = new Float32Array(this.N * this.N * 2);
    this._gpuBuffer = new Uint16Array(this.N * this.N * 4); // RGBA half-float
    this.texture = new THREE.DataTexture(
      this._gpuBuffer, this.N, this.N,
      THREE.RGBAFormat, THREE.HalfFloatType,
    );
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.needsUpdate = true;
  }

  get stats(): Readonly<InfluenceMapStats> { return this._stats; }

  /**
   * Apply exponential time decay to the full map. Call once per frame
   * BEFORE depositing agents.
   */
  decay(dt: number): void {
    const factor = Math.exp(-this.config.decay * dt);
    if (factor >= 0.9999) return; // skip near-identity multiply for tiny dt
    for (let i = 0; i < this.bend.length; i++) {
      this.bend[i] *= factor;
    }
  }

  /**
   * Deposit one agent's footprint into the map.
   *
   * The bend direction:
   *   - With explicit `directionX/Z`: deposit (dirX, dirZ) * strength * gauss
   *   - Without: deposit a radial outward push (cell - agent) / r * strength * gauss
   */
  depositAgent(agent: InfluenceAgent): void {
    const N = this.N;
    const b = this.config.bounds;
    // (N-1) spacing to match sampleBilinear's grid mapping (N samples span the
    // bounds inclusively); /N left deposits offset ~0.5 cell from queries.
    const sx = (b.maxX - b.minX) / (N - 1);
    const sz = (b.maxZ - b.minZ) / (N - 1);
    const cellSize = Math.min(sx, sz);
    const gridR = agent.radius / cellSize;
    const gridR2 = gridR * gridR;
    // WHY per-axis radii: with non-square bounds sx ≠ sz, and a single
    // grid-space radius derived from the SMALLER spacing made the footprint a
    // circle in grid space = an ellipse in world space (2× too wide along the
    // coarser axis). Measure the footprint in world metres on each axis.
    // Square bounds (sx === sz) reduce to exactly the old arithmetic.
    const gridRx = agent.radius / sx;
    const gridRz = agent.radius / sz;
    const ax = sx / cellSize;   // world-metres-per-cell ratio, ≥ 1
    const az = sz / cellSize;

    // Convert agent world XZ to fractional grid coords.
    const gx = (agent.x - b.minX) / sx;
    const gz = (agent.z - b.minZ) / sz;
    const i0 = Math.max(0, Math.floor(gx - gridRx));
    const i1 = Math.min(N - 1, Math.ceil(gx + gridRx));
    const j0 = Math.max(0, Math.floor(gz - gridRz));
    const j1 = Math.min(N - 1, Math.ceil(gz + gridRz));

    const sigma2 = Math.max(0.25, gridR2);
    const useExplicitDir = agent.directionX !== undefined && agent.directionZ !== undefined;
    const dirX = agent.directionX ?? 0;
    const dirZ = agent.directionZ ?? 0;
    const cap = this.config.maxBend;

    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        // Offsets in units of the finer cell so the radius test is isotropic
        // in WORLD space (identical to the old dx/dz when sx === sz).
        const dx = (i - gx) * ax;
        const dz = (j - gz) * az;
        const r2 = dx * dx + dz * dz;
        if (r2 > gridR2) continue;
        // Gaussian footprint amplitude.
        const amp = Math.exp(-r2 / sigma2) * agent.strength;
        let bx: number, bz: number;
        if (useExplicitDir) {
          bx = dirX * amp;
          bz = dirZ * amp;
        } else {
          // Radial outward — push cell AWAY from agent.
          const r = Math.sqrt(r2) + 1e-6;
          bx = (dx / r) * amp;
          bz = (dz / r) * amp;
        }
        const cellIdx = (j * N + i) * 2;
        let nx = this.bend[cellIdx]     + bx;
        let nz = this.bend[cellIdx + 1] + bz;
        // Cap magnitude to maxBend.
        const mag = Math.sqrt(nx * nx + nz * nz);
        if (mag > cap) {
          const scale = cap / mag;
          nx *= scale;
          nz *= scale;
        }
        this.bend[cellIdx]     = nx;
        this.bend[cellIdx + 1] = nz;
      }
    }
    this._stats.agentsThisFrame++;
  }

  /** Reset agent counter (call once per frame before depositing). */
  beginFrame(): void {
    this._stats.agentsThisFrame = 0;
  }

  /**
   * Pack the Float32 bend buffer into the half-float GPU texture. Updates
   * stats. Call once per frame after all deposits.
   */
  upload(): void {
    const t0 = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const N = this.N;
    let totalEnergy = 0;
    let maxBend = 0;
    for (let i = 0; i < N * N; i++) {
      const bx = this.bend[i * 2];
      const bz = this.bend[i * 2 + 1];
      const mag = Math.sqrt(bx * bx + bz * bz);
      if (mag > maxBend) maxBend = mag;
      totalEnergy += mag;
      this._gpuBuffer[i * 4]     = floatToHalf(bx);
      this._gpuBuffer[i * 4 + 1] = floatToHalf(bz);
      this._gpuBuffer[i * 4 + 2] = 0;
      this._gpuBuffer[i * 4 + 3] = 0;
    }
    this.texture.needsUpdate = true;
    this._stats.totalEnergy = totalEnergy;
    this._stats.maxBend = maxBend;
    const t1 = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    this._stats.updateTimeMs = t1 - t0;
  }

  /** Sample the bend at a world-space XZ point. Returns (bendX, bendZ). */
  sampleBilinear(worldX: number, worldZ: number): { x: number; z: number } {
    const N = this.N;
    const b = this.config.bounds;
    const gx = (worldX - b.minX) / (b.maxX - b.minX) * (N - 1);
    const gz = (worldZ - b.minZ) / (b.maxZ - b.minZ) * (N - 1);
    if (gx < 0 || gz < 0 || gx > N - 1 || gz > N - 1) {
      return { x: 0, z: 0 };
    }
    const i0 = Math.floor(gx);
    const j0 = Math.floor(gz);
    const i1 = Math.min(N - 1, i0 + 1);
    const j1 = Math.min(N - 1, j0 + 1);
    const fx = gx - i0;
    const fz = gz - j0;
    const idx = (i: number, j: number, c: 0 | 1) => (j * N + i) * 2 + c;
    const c00x = this.bend[idx(i0, j0, 0)];
    const c10x = this.bend[idx(i1, j0, 0)];
    const c01x = this.bend[idx(i0, j1, 0)];
    const c11x = this.bend[idx(i1, j1, 0)];
    const c00z = this.bend[idx(i0, j0, 1)];
    const c10z = this.bend[idx(i1, j0, 1)];
    const c01z = this.bend[idx(i0, j1, 1)];
    const c11z = this.bend[idx(i1, j1, 1)];
    const x = (c00x * (1 - fx) + c10x * fx) * (1 - fz)
            + (c01x * (1 - fx) + c11x * fx) * fz;
    const z = (c00z * (1 - fx) + c10z * fx) * (1 - fz)
            + (c01z * (1 - fx) + c11z * fx) * fz;
    return { x, z };
  }

  /** Reset the entire map to upright (no bend). */
  clear(): void {
    this.bend.fill(0);
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
  }
}

// IEEE 754 single → half-precision float (same routine as SmokeTextureSync).
function floatToHalf(val: number): number {
  if (!Number.isFinite(val)) {
    if (Number.isNaN(val)) return 0x7e00;
    return val > 0 ? 0x7c00 : 0xfc00;
  }
  if (val === 0) return 0;
  _f32[0] = val;
  const x = _i32[0];
  const sign = (x >> 16) & 0x8000;
  const exp = ((x >> 23) & 0xff) - (127 - 15);
  const mant = (x & 0x7fffff);
  if (exp <= 0) {
    if (exp < -10) return sign;
    const shifted = (mant | 0x800000) >> (1 - exp);
    return sign | (shifted >> 13);
  } else if (exp >= 31) {
    return sign | 0x7c00;
  }
  return sign | (exp << 10) | (mant >> 13);
}

const _f32 = new Float32Array(1);
const _i32 = new Int32Array(_f32.buffer);
